import { IconNames } from '@blueprintjs/icons';
import { type BenefitType, BenefitTypeDisplayTextMap } from '@strata/constants';
import { DateFormats, formatDate, useDebouncedFunction } from '@strata/helper-functions';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import { type ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';

import { useUnreadCounts } from 'services/APIHooks';
import { AppRoutes } from 'services/routes';
import { type ReferralConversationCounts } from 'services/userSupportHelpers';
import {
    type ExpiredReferral,
    useGetExpiredReferralFilterOptionsQuery,
    useGetExpiredReferralsCountQuery,
    useGetPagedExpiredReferralsQuery,
} from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties = [{ id: 'referralExpiration', desc: true }];

const customSearchValueHandlers: ComponentProps<
    typeof GridWithFilters<ExpiredReferral>
>['customSearchValueHandlers'] = {
    referralExpiration: (value) => formatDate(value, DateFormats.DATE_TIME),
};

const columns = ({
    referralConversationCounts,
}: {
    referralConversationCounts?: ReferralConversationCounts;
}): ColumnDef<ExpiredReferral>[] => [
    {
        accessorKey: 'product',
        header: 'Product',
        size: 140,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
        ),
    },
    {
        accessorKey: 'patient',
        header: 'Patient',
        size: 100,
    },
    {
        accessorKey: 'memberId',
        header: 'Member ID',
        size: 100,
    },
    {
        accessorKey: 'segmentName',
        header: 'Health Plan',
        size: 100,
    },
    {
        accessorKey: 'valueCases',
        header: 'Attributes',
        size: 80,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={[
                    ...(getValue() ?? []),
                    ...(row.original.hasCopayAssistance
                        ? [{ strat_rule_name: PatientInfoList.Types.COPAY }]
                        : []),
                ]}
                incentiveTotal={row.original.incentiveTotal}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'referralId',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 45,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'referralExpiration',
        header: 'Expiration Date',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

export const ExpiredReferralListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();

    const { referralConversationCounts } = useUnreadCounts();

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.EXPIRED_REFERRALS, initialSortProperties);

    // The search box updates `search` on every keystroke to keep the input responsive, but the
    // referrals queries are expensive, so only feed the API a debounced copy — otherwise each
    // keystroke fires a concurrent query and saturates the DB.
    const debounceSearchText = useDebouncedFunction();
    const [debouncedSearchText, setDebouncedSearchText] = useState(search);

    const apiQueryParams = {
        page,
        searchText: debouncedSearchText,
        sort,
        ...activeFilters,
    };
    const countQueryParams = {
        searchText: debouncedSearchText,
        ...activeFilters,
    };

    // Count is loaded asynchronously so the grid is not blocked waiting for totalCount.
    const { data: countData } = useGetExpiredReferralsCountQuery(countQueryParams);
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetPagedExpiredReferralsQuery(apiQueryParams);
    const { data: filterOptions, isFetching: isFilterOptionsFetching } =
        useGetExpiredReferralFilterOptionsQuery();

    const { data: segmentsData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segmentsData ?? {};

    const filterCategories: FilterCategory<ExpiredReferral>[] = [
        {
            key: 'segment_id',
            title: 'Health Plan',
            customValues: filterOptions?.segment_id?.map((option) => ({
                value: option,
                text: payerSegmentMap?.[option]?.name,
            })),
        },
        {
            key: 'product_name',
            title: 'Product',
            customValues: filterOptions?.product_name?.map((option) => ({
                value: option,
            })),
        },
        {
            key: 'benefit',
            title: 'Benefit',
            customValues: filterOptions?.benefit?.map((option) => ({
                value: option,
                text: BenefitTypeDisplayTextMap[option as BenefitType],
            })),
        },
    ];

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Expired Referrals"
                secondaryTitle={loggedInSupplierName}
                activeFilters={activeFilters}
                columns={columns({ referralConversationCounts })}
                currentPage={page}
                data={currentPageData?.referrals}
                enableSearch
                filterCategories={filterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={
                    isCurrentPageDataFetching ||
                    isFilterOptionsFetching ||
                    search !== debouncedSearchText
                }
                manualSorting
                onFilter={updateGridActiveFilters}
                onFilterPaneResize={updateFilterPaneWidth}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => navigate(AppRoutes.wonReferralDetails(rowData.referralId))}
                onSearch={(value) => {
                    updateGridSearch?.(value);
                    debounceSearchText(() => setDebouncedSearchText(value));
                }}
                onSort={updateGridSort}
                customSearchValueHandlers={customSearchValueHandlers}
                searchText={search}
                sortProperties={sort}
                totalCount={countData}
            />
        </FormPageContainer>
    );
};
